interface Credentials {
  accessKey: string;
  secretKey: string;
  sessionToken: string;
  bucket?: string;
  quota_limit?: number;
  expiration_date?: string;
}

interface Share {
  id: number;
  access_token: string;
  owner_external_id: number;
}

function getApiBaseUrl(): string {
  return process.env.REACT_APP_API_BASE_URL || '';
}

export async function getShareById(shareId: string): Promise<Share | null> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/sharas/by-user/${shareId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      new Error(`Failed to get shares: ${response.status} ${response.statusText}`);
    }

    const shares = await response.json();

    return shares.length > 0 ? shares[0] : null;
  } catch (error) {
    console.error('Error getting shares:', error);
    throw error;
  }
}

export async function createShare(userId: number = 1): Promise<Share> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/sharas/create/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      new Error(`Failed to create share: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating share:', error);
    throw error;
  }
}

export async function getCredentialsForShare(accessToken: string): Promise<Credentials> {
  try {
    const response = await fetch(`${getApiBaseUrl()}/sharas/get_sts/${accessToken}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.detail || `Failed to get credentials: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const credentials = result.AssumeRoleWithWebIdentityResult.Credentials;

    return {
      accessKey: credentials.AccessKeyId,
      secretKey: credentials.SecretAccessKey,
      sessionToken: credentials.SessionToken,
      bucket: result.bucket,
      quota_limit: result.quota_limit,
      expiration_date: result.expiration_date,
    };
  } catch (error) {
    console.error('Error getting credentials:', error);
    throw error;
  }
}

export async function getOrCreateShareAndCredentials(userId?: string): Promise<{
  share: Share;
  credentials: Credentials
}> {
  let share: Share;
  const userIdNum = userId ? parseInt(userId) : 1;

  if (userId) {
    const existingShare = await getShareById(userId);
    if (existingShare) {
      share = existingShare;
    } else {
      share = await createShare(userIdNum);
    }
  } else {
    share = await createShare(1);
  }

  const credentials = await getCredentialsForShare(share.access_token);

  return {share, credentials};
}
